import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { MovieRail } from "@/components/MovieRail";
import { AdBanner } from "@/components/AdBanner";
import { Play, Info, Star } from "lucide-react";

const Home = () => {
  const [movies, setMovies] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [mRes, fRes, cRes] = await Promise.all([
          api.get("/movies"),
          api.get("/movies/featured"),
          api.get("/categories"),
        ]);
        setMovies(mRes.data);
        setFeatured(fRes.data);
        setCategories(cRes.data.filter((c) => c !== "All"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const hero = featured[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#00F0FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] pb-20">
      {hero && (
        <section className="relative h-[80vh] min-h-[520px] w-full" data-testid="hero-section">
          <img src={hero.backdrop_url} alt={hero.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/90 via-[#030303]/30 to-transparent" />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="relative z-10 h-full flex flex-col justify-end max-w-[1600px] mx-auto px-4 sm:px-8 pb-16 md:pb-24 max-w-full"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono-ui text-[10px] tracking-widest px-2 py-1 rounded border border-[#00F0FF]/40 text-[#00F0FF]">{hero.quality}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/60">{hero.genre}</span>
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white max-w-3xl text-glow">
              {hero.title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-white/70">
              <span className="flex items-center gap-1"><Star size={14} className="text-[#00F0FF]" fill="currentColor" />{hero.rating?.toFixed(1)}</span>
              <span>{hero.release_year}</span>
              <span>{hero.origin}</span>
            </div>
            <p className="mt-4 text-sm md:text-base text-white/70 max-w-xl line-clamp-3">{hero.description}</p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to={`/watch/${hero.id}`} data-testid="hero-play-btn" className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF] text-black font-semibold px-7 py-3 hover:scale-105 transition-transform glow-cyan">
                <Play size={20} fill="currentColor" /> Play Now
              </Link>
              <Link to={`/movie/${hero.id}`} data-testid="hero-info-btn" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white font-medium px-7 py-3 hover:bg-white/20 transition-colors">
                <Info size={20} /> More Info
              </Link>
            </div>
          </motion.div>
        </section>
      )}

      <div className="relative z-20 -mt-8 md:-mt-16">
        <MovieRail title="Trending Now" movies={movies.filter((m) => m.category === "Trending")} testid="rail-trending" />
        <AdBanner className="mb-10" />
        {categories.map((cat) => (
          <MovieRail key={cat} title={cat} movies={movies.filter((m) => m.category === cat)} testid={`rail-${cat.toLowerCase()}`} />
        ))}
        <MovieRail title="All Titles" movies={movies} testid="rail-all" />
      </div>
    </div>
  );
};

export default Home;
